import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { NavLink } from "react-router-dom";
import { formatTime } from "../../../shared/format-time";
import PageToEighth from "../../../shared/eighth";
import { OnboardingStep3ClassName } from "./ducks/reducer";
import { color } from "d3";
import Fork from "../../icons/fork";
import Chain from "../../icons/chain";
import Delete from "../../icons/delete";

export const getSceneItemStyleFromTimeOfDay = (
    dayNight,
    interiorExterior
) => {
    let backgroundColor = "#040f21"; // default background color
    let foregroundColor = "#ffffff"; // default background color    
    if (interiorExterior === "Int.") {
        switch (dayNight) {
            case 1: // Day                  
                backgroundColor = "#ffffff"; // white
                foregroundColor = "#040f21";
                break;
            case 2: // Night
                backgroundColor = "#040f21"; // dark blue
                break;
            case 4: // Sunrise
                backgroundColor = "#FDABD3"; // Pink
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 5: // Sunset
                backgroundColor = "#FEF3B3"; // light orange
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 10: // Early Morning
            case 6: // Morning
            case 11: // Late Morning
                backgroundColor = "#FDABD3"; // Pink
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 12: // Early Afternoon
            case 8: // Afternoon
            case 13: // Late Afternoon
                backgroundColor = "#1DB5AB"; //aqua
                break;
            case 14: // Early Evening
            case 9: // Evening
            case 15: // Late Evening
                backgroundColor = "#040f21"; // dark blue
                break;
        }
    } else if (interiorExterior === "Ext.") {
        switch (dayNight) {
            case 1: // Day                  
                backgroundColor = "#F9E26E"; // yellow
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 2: // Night
                backgroundColor = "#8bc34a"; // dark green
                break;
            case 4: // Sunrise
                backgroundColor = "#FDABD3"; // Pink
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 5: // Sunset
                backgroundColor = "#FE9F5D"; // burnt orange
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 10: // Early Morning
            case 6: // Morning
            case 11: // Late Morning
                backgroundColor = "#B9D359"; // light green
                foregroundColor = "#040f21"; // dark text for contrast
                break;
            case 12: // Early Afternoon
            case 8: // Afternoon
            case 13: // Late Afternoon
                backgroundColor = "#ae84ea"; // light purple
                break;
            case 14: // Early Evening
            case 9: // Evening
            case 15: // Late Evening
                backgroundColor = "#8bc34a"; // dark green
                break;
        }
    }

    return {
        backgroundColor,
        foregroundColor
    }
}


export const getSceneItemStyle = (
    isDragging,
    draggableStyle,
    isSelected,
    isGhosting,
    dayNight,
    interiorExterior,
    colorCode
) => {
    let backgroundColor = "#040f21"; // default background color
    let foregroundColor = "#ffffff"; // default background color
    if (colorCode) {
        const colors = getSceneItemStyleFromTimeOfDay(dayNight, interiorExterior);
        backgroundColor = colors.backgroundColor;
        foregroundColor = colors.foregroundColor;
    }

    return {
        // some basic styles to make the items look a bit nicer
        userSelect: "none",
        //padding: grid * 2,
        //margin: `0 0 ${grid}px 0`,
        borderRadius: "5px",
        maxHeight: isDragging ? "3rem" : "auto",
        // change background colour if dragging

        background: isGhosting
            ? "black"
            : isSelected
                ? "#222E44"
                : isDragging
                    ? "#222E44"
                    : backgroundColor,
        color: foregroundColor,
        overflow: "hidden",
        // styles we need to apply on draggables
        ...draggableStyle
    }
}


const Scene = ({
    filmId,
    index,
    scene,
    toggleSelection,
    toggleSelectionInGroup,
    multiSelectTo,
    isSelected,
    isGhosting,
    readOnly,
    truncateSummary,
    colorCode,
    onDuplicateScene,
    isSceneInWithoutDays,
    onDeleteSceneInstance,
    canDeleteScene,
    sceneInstanceCount
}) => {


    const primaryButton = 0;

    // Using onClick as it will be correctly
    // preventing if there was a drag
    const onClick = (event: any) => {
        if (readOnly) {
            return;
        }

        if (event.defaultPrevented) {
            return;
        }

        if (event.button !== primaryButton) {
            return;
        }

        // marking the event as used
        event.preventDefault();

        performAction(event);
    };

    // Determines if the platform specific toggle selection in group key was used
    const wasToggleInSelectionGroupKeyUsed = (
        event: MouseEvent | KeyboardEvent
    ) => {
        const isUsingWindows = navigator.platform.indexOf("Win") >= 0;
        return isUsingWindows ? event.ctrlKey : event.metaKey;
    };

    // Determines if the multiSelect key was used
    const wasMultiSelectKeyUsed = (event: MouseEvent | KeyboardEvent) =>
        event.shiftKey;

    const performAction = (event: MouseEvent | KeyboardEvent) => {
        if (wasToggleInSelectionGroupKeyUsed(event)) {
            toggleSelectionInGroup(scene._instanceId);
            return;
        }

        if (wasMultiSelectKeyUsed(event)) {
            multiSelectTo(scene._instanceId);
            return;
        }

        toggleSelection(scene._instanceId);
    };

    const size: number = 30;
    const grid = 8;

    return (
        <Draggable
            key={scene._instanceId}
            //draggableId={scene.sceneId + day.number + index + scene.locationId + scene.subLocationId}
            draggableId={scene._instanceId}
            index={index}
            isDragDisabled={readOnly}>
            {(provided, snapshot) => {
                const style = getSceneItemStyle(
                    snapshot.isDragging,
                    provided.draggableProps.style,
                    isSelected,
                    isGhosting,
                    scene.dayNight,
                    scene.interiorExterior,
                    colorCode
                )
                return (
                    <>
                        <tr
                            className={index === 0 ? OnboardingStep3ClassName : ""}
                            onClick={(e) => onClick(e)}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={style}>
                            <>
                                {snapshot.isDragging ? (<>
                                    <td>
                                        {!readOnly && <i className="material-icons">
                                            drag_indicator
                                        </i>}
                                        {scene.number}
                                    </td>
                                    <td>
                                        {scene.settingName}
                                    </td>
                                    <td>
                                        {scene.locationName}{scene.subLocationName && <span> - {scene.subLocationName}</span>}
                                    </td>
                                    <td>{scene.interiorExterior}</td>
                                    <td>{scene.dayNightDescription}</td>
                                </>) :
                                    (<>

                                        <td>
                                            {!readOnly && <i className="material-icons">
                                                drag_indicator
                                            </i>}
                                            <NavLink
                                                to={`/films/${filmId}/scenes/${scene.sceneId}/edit`} style={{ color: style.color }}>{scene.number}</NavLink>
                                            {false && sceneInstanceCount > 1 && (
                                                <span className="chain-icon" title={`Appears on ${sceneInstanceCount} days`} style={{ marginLeft: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }}>
                                                    <Chain style={{ width: '14px', height: '14px', color: style.color }} />
                                                </span>
                                            )}
                                        </td>
                                        <td className={`${truncateSummary ? "truncated-summary" : ""}`} title={scene.settingName}>
                                            <NavLink
                                                to={`/films/${filmId}/scene-settings/${scene.settingId}/edit`}
                                                style={{ color: style.color }}
                                            >
                                                {scene.settingName}
                                            </NavLink>
                                        </td>
                                        <td className={`${truncateSummary ? "truncated-summary" : ""}`} title={scene.locationName}>
                                            <NavLink
                                                to={`/films/${filmId}/locations/${scene.locationId}/edit`}
                                                style={{ color: style.color }}
                                            >
                                                {scene.locationName}{scene.subLocationName && <span> - {scene.subLocationName}</span>}
                                            </NavLink>
                                        </td>
                                        <td>{scene.interiorExterior}</td>
                                        <td>{scene.dayNightDescription}</td>
                                        <td className={`${truncateSummary ? "truncated-summary" : ""}`} title={scene.summary}>{scene.summary}</td>
                                        <td>
                                            <PageToEighth
                                                page={scene.pageLength}
                                            ></PageToEighth>
                                        </td>
                                        <td>
                                            {Math.round(scene.shootDays * 10) / 10}
                                        </td>
                                        <td>
                                            {formatTime(scene.estimatedShootingDuration)}
                                        </td>
                                        <td className={`${truncateSummary ? "truncated-summary" : ""}`} title={(scene.characters || []).join(", ")}>
                                            {(scene.characters || []).join(", ")}
                                        </td>
                                        <td>{scene.scriptDay}</td>
                                        <td className="scene-actions">
                                            {!readOnly && (
                                                <>
                                                    {!isSceneInWithoutDays && (
                                                        <span
                                                            className="button is-small is-text duplicate-scene-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDuplicateScene(scene);
                                                            }}
                                                            title="Duplicate scene to unscheduled scenes">
                                                            <span className="icon">
                                                                <Fork style={{ width: '16px', height: '16px', color: style.color }} />
                                                            </span>
                                                        </span>
                                                    )}
                                                    {canDeleteScene && (
                                                        <span
                                                            className="button is-small is-text delete-scene-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDeleteSceneInstance(scene._instanceId);
                                                            }}
                                                            title="Delete this scene instance">
                                                            <span className="icon">
                                                                <Delete style={{ width: '16px', height: '16px', marginRight: 0, color: style.color }} />
                                                            </span>
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </>)}
                            </>
                        </tr>
                    </>
                )
            }}
        </Draggable>
    );
};

export default Scene;
